import type { DrawerTarget, DrawerSection } from './objectDrawerTypes'
export type { DrawerTarget, DrawerSection } from './objectDrawerTypes'
import { loadDocumentMetadata } from '../lib/documentMetadata'
import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react'
import { useAuthSession } from '../lib/authSession'
import EvidenceGapPane from './evidence/EvidenceGapPane'
import type { RecordEvidenceCard, RecordEvidenceContext } from './navigation/recordEvidenceContext'
import type { ContextRequirement } from './workbench/contextWork'
import { Link } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import { obligationLabel } from '../lib/claims'
import type { TaskQueueItem } from '../lib/tasks'
import { HelpDot } from './guidance/Guidance'
import Sentence, { LinkedSentence } from './Sentence'
import { articleFor, lawWords, natureWords } from '../lib/sentences/words'
import { RECON_TASK_TYPES, taskDelta } from './fund/recordDerive'
import type { WalkNode } from './fund/fundArea'
import { ErrorState, Skeleton } from './state'
import RemovalForm from './recordRemovals/RemovalForm'

/* Object drawer — ONE PAGE, option A (Ale rulings 2026-09-03).
 *
 * The drawer is ONE OBJECT: what it is, what proves it, what it still
 * owes — it never says anything about the fund as a whole (the chain
 * status block does), and it fits one page at 1440 × 900 without
 * scrolling. Its lines, top to bottom:
 *
 *   kind tag + name
 *   the section sentence + the two doors (Show on diagram / Show in record)
 *   WHAT IT IS  — one sentence, the record's values as data
 *   PROVEN      — one line per demand met: demand — paper, validity · code
 *                 (the paper is the door to the document page; a waiver is
 *                 the reason on that line — waived counts as proven)
 *   "also named in N documents ›" — one door to the mentions
 *   OWED        — one line per gap with its verb-door: request it › /
 *                 chase › / sign it off › / reconcile › / Look-through ›
 *
 * Nothing else: no excerpts (the document page shows them), no per-mention
 * rows, no key/value facts, no history (the record row's ledger keeps it),
 * no vocabulary, no to-do pills. The tool is never a person. */

const TARGET_WORD: Record<DrawerTarget['type'], string> = {
  entity: 'entity',
  instrument: 'instrument',
  position: 'position',
  trade: 'movement',
}

interface EvidenceRow {
  id: string
  document_id: string
  document_filename: string | null
  evidential_purpose: string
  evidential_weight: string
  document_date: string | null
  valid_from: string | null
  valid_to: string | null
  validity_status: string
  is_signed: boolean | null
  source_page: number | null
  source_text: string | null
}

interface DrawerResponse {
  target_type: string
  target_id: string
  title: string
  subtitle: string | null
  facts: Record<string, string | number | boolean>
  evidence: EvidenceRow[]
  history: unknown[]
}

interface ObligationStatusRow {
  id: string
  obligation_code: string
  status: string
  satisfied_by_document_id: string | null
  waived_reason: string | null
  waived_at: string | null
  document_date?: string | null
  obligation: {
    code: string
    is_mandatory: boolean
    stale_after_months?: number | null
  } | null
}

type Facts = Record<string, string | number | boolean>

function pick(facts: Facts, ...keys: string[]): string | null {
  for (const k of keys) {
    const alt = k.replace(/ /g, '_')
    for (const kk of [k, alt]) {
      if (kk in facts && facts[kk] !== '' && facts[kk] !== null) return String(facts[kk])
    }
  }
  return null
}

/** A backend money fact ("25000000.000000 EUR") as the compiler's money
 *  value — the currency LEADS the figure ("EUR 25,000,000"). */
function moneyFact(v: string | null): { amount: string | null; currency?: string | null } | null {
  if (v === null) return null
  const m = /^(-?\d+(?:\.\d+)?)\s+([A-Z]{3})$/.exec(v.trim())
  return m ? { amount: m[1], currency: m[2] } : { amount: v }
}

function lawSlot(jurisdiction: string | null): string | null {
  const w = lawWords(jurisdiction)
  return w.adjective ?? (w.of ? `the ${w.of}` : null)
}

/* ── WHAT IT IS — one sentence ─────────────────────────────────────────── */

function FactSentence({
  target,
  title,
  facts,
}: {
  target: DrawerTarget
  title: string
  facts: Facts
}) {
  const props = {
    as: 'p' as const,
    className: 'drawer-fact',
    'data-testid': 'drawer-fact-sentence',
  }
  if (target.type === 'entity') {
    const legalForm = pick(facts, 'legal form')
    return (
      <Sentence
        {...props}
        meaning={legalForm ? 'object.fact.entity' : 'object.fact.entity.noform'}
        values={{
          entity: title,
          article: legalForm ? articleFor(legalForm) : null,
          legal_form: legalForm,
          law: lawSlot(pick(facts, 'jurisdiction')),
          nature: natureWords(pick(facts, 'nature')),
        }}
      />
    )
  }
  if (target.type === 'instrument')
    return (
      <Sentence
        {...props}
        meaning="object.fact.instrument"
        values={{
          instrument: title,
          category: pick(facts, 'category'),
          issuer: pick(facts, 'issuer'),
          outstanding: pick(facts, 'outstanding units'),
          currency: pick(facts, 'currency'),
          as_of: pick(facts, 'balance as of'),
        }}
      />
    )
  if (target.type === 'position') {
    const committed = pick(facts, 'committed')
    const since = pick(facts, 'effective from')
    if (committed)
      return (
        <Sentence
          {...props}
          meaning={since ? 'object.fact.commitment.dated' : 'object.fact.commitment'}
          values={{
            holder: pick(facts, 'holder'),
            committed: moneyFact(committed),
            instrument: pick(facts, 'instrument'),
            issuer: pick(facts, 'issuer'),
            since,
            drawn: moneyFact(pick(facts, 'drawn (derived)', 'drawn')),
            undrawn: moneyFact(pick(facts, 'undrawn')),
          }}
        />
      )
    return (
      <Sentence
        {...props}
        meaning="object.fact.position"
        values={{
          holder: pick(facts, 'holder'),
          units: pick(facts, 'units held'),
          instrument: pick(facts, 'instrument'),
          issuer: pick(facts, 'issuer'),
          pct: pick(facts, 'economic interest %'),
          since,
        }}
      />
    )
  }
  return (
    <Sentence
      {...props}
      meaning="object.fact.trade"
      values={{
        type: title.toLowerCase(),
        date: pick(facts, 'effective date'),
        units: pick(facts, 'units delta'),
        amount: { amount: pick(facts, 'amount'), currency: pick(facts, 'currency') },
        holder: pick(facts, 'position'),
        document: pick(facts, 'source document'),
      }}
    />
  )
}

/* ── the lines ─────────────────────────────────────────────────────────── */

function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const total = (y * 12 + (m - 1) + months) as number
  const yy = Math.floor(total / 12)
  const mm = (total % 12) + 1
  return `${yy}-${String(mm).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function SectionHead({
  children,
  first,
  count,
  countTestId,
}: {
  children: React.ReactNode
  first?: boolean
  count?: string
  countTestId?: string
}) {
  return (
    <div className={`drawer-sec${first ? ' first' : ''}`}>
      {children}
      {count !== undefined ? (
        <span className="drawer-cnt" data-testid={countTestId}>
          {count}
        </span>
      ) : null}
    </div>
  )
}

/* The document names the proven lines need: from the drawer's own
 * metadata, including documents already represented by an EvidenceItem.
 * The uploaded filename is never a document display-title fallback. */
function documentName(id: string): Promise<string | null> {
  return loadDocumentMetadata(id)
    .then((doc) => doc.display_title ?? null)
    .catch(() => null)
}

const PROVEN = new Set(['satisfied', 'waived'])
const OWED = new Set(['open', 'requested', 'pending_signoff'])
/** Lines a section shows before folding the rest into "… N more ›". */
const CAP = 5

export default function ObjectDrawer({
  fundId,
  target,
  section,
  tasks = [],
  walk = [],
  relatedIds = [],
  onShowOnDiagram,
  onShowInRecord,
  contextNavigation,
  recordEditor,
  recordRevision = 0,
  evidenceContext,
  workContext,
  addMovementHref,
  onClose,
  allowRemoval = false,
  removalRequestIds = [],
  onRemovalRequested,
  onRemoved,
}: {
  fundId: string
  target: DrawerTarget
  section?: DrawerSection | null
  /** Open workbench tasks on this fund — the breaks and chases that make
   *  the OWED lines' doors. */
  tasks?: TaskQueueItem[]
  /** The walk status — an entity with an open look-through owes it. */
  walk?: WalkNode[]
  /** The other records of the same object (the undrawn record of a
   *  commitment package): a break on either is owed by the one position. */
  relatedIds?: string[]
  onShowOnDiagram?: () => void
  onShowInRecord?: () => void
  /** Exact admitted contextual doors; absent for the legacy drawer. */
  contextNavigation?: ReactNode
  /** Current Record only; the editor retains its own exact admission and result. */
  recordEditor?: ReactNode
  recordRevision?: number
  evidenceContext?: RecordEvidenceContext
  workContext?: { evidenceHref: string; chaseHref: string }
  addMovementHref?: string
  onClose: () => void
  /** Explicitly granted only by a current, recorded viewing context. */
  allowRemoval?: boolean
  removalRequestIds?: readonly string[]
  onRemovalRequested?: () => void
  onRemoved?: () => void
}) {
  const session = useAuthSession()
  const scopeKey = JSON.stringify([session, fundId, target.type, target.id])
  const [evidencePending, setEvidencePending] = useState(false)
  const pendingEvidence = useRef(false)
  const onEvidencePending = useCallback((pending: boolean) => {
    pendingEvidence.current = pending
    setEvidencePending(pending)
  }, [])
  const [selectedEvidence, setSelectedEvidence] = useState<{
    scopeKey: string
    requirementId: string
    card?: RecordEvidenceCard
    requirement?: ContextRequirement
  } | null>(null)
  const selection = selectedEvidence?.scopeKey === scopeKey ? selectedEvidence : null
  const [data, setData] = useState<DrawerResponse | null>(null)
  const [detailReadKey, setDetailReadKey] = useState<string | null>(null)
  const [removalSelection, setRemovalSelection] = useState<string | null>(null)
  const removalKey = `${fundId}:${target.type}:${target.id}`
  const showRemoval = removalSelection === removalKey
  const canRemove =
    allowRemoval && data?.target_type === target.type && data?.target_id === target.id
  /* 3A + the keyboard canon (Ale 2026-09-04): the drawer is a dialog over
   * the page. Focus lands on it when it opens; Esc closes it; when it
   * leaves — Esc, ✕, or the page clearing the focus — focus goes back to
   * the row, box or cell that opened it. The pattern of the trade drawer
   * (RecordTable). Captured ONCE per opening: ‹ prev / next › inside keep
   * the original opener. */
  const panel = useRef<HTMLElement | null>(null)
  const opener = useRef<Element | null>(null)
  // Keep the mount-scoped Escape listener on the current URL-aware close
  // callback without recapturing the original opener on navigation.
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    opener.current = document.activeElement
    panel.current?.focus()
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' || ev.defaultPrevented) return
      if (pendingEvidence.current) {
        ev.preventDefault()
        return
      }
      ev.preventDefault()
      closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      const back = opener.current
      if (back instanceof HTMLElement && document.contains(back)) back.focus()
    }
    // Mount-scoped on purpose: focus is captured once, per drawer opening.
  }, [])
  const [requirementRead, setRequirementRead] = useState<{
    key: string
    rows: ObligationStatusRow[] | null
    error?: unknown
  } | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [docNames, setDocNames] = useState<Record<string, string | null>>({})
  const [tick, setTick] = useState(0)
  const evidenceReadKey = JSON.stringify([scopeKey, tick, recordRevision])
  const currentRequirements = requirementRead?.key === evidenceReadKey ? requirementRead : null
  const rows = currentRequirements?.rows ?? null
  const requirementError = currentRequirements?.error
  const evidenceAvailable =
    !!evidenceContext?.available &&
    rows !== null &&
    !requirementError &&
    !error &&
    detailReadKey === evidenceReadKey &&
    data?.target_type === target.type &&
    data.target_id === target.id
  const choices =
    evidenceContext?.cards.filter((card) =>
      card.requirements.some((requirement) => requirement.id === selection?.requirementId)
    ) ?? []
  const selectedCard = choices.find((card) => card.id === selection?.card?.id)
  const selectedRequirement = selectedCard?.requirements.find(
    (row) => row.id === selection?.requirementId
  )
  // Keep the exact document set present when a request starts. A concurrent
  // refresh cannot remove its decision controller before the receipt returns.
  useEffect(() => {
    if (evidencePending || !selectedCard || !evidenceAvailable) return
    setSelectedEvidence((current) =>
      current?.scopeKey === scopeKey &&
      current.card?.id === selectedCard.id &&
      current.card !== selectedCard
        ? { ...current, card: selectedCard }
        : current
    )
  }, [evidencePending, selectedCard, evidenceAvailable, scopeKey])
  const panelCard = selection?.card
  const panelRequirement = selectedRequirement ?? selection?.requirement
  const panelAvailable =
    evidenceAvailable &&
    !!selectedCard &&
    !!selectedRequirement &&
    rows!.some((row) => row.id === selection?.requirementId)
  const chooseEvidence = (requirementId: string, card?: RecordEvidenceCard) => {
    if (!evidenceAvailable || evidencePending) return
    const eligible = evidenceContext!.cards.filter((choice) =>
      choice.requirements.some((row) => row.id === requirementId)
    )
    const chosen = card
      ? eligible.find((choice) => choice.id === card.id)
      : eligible.length === 1
        ? eligible[0]
        : undefined
    setSelectedEvidence({
      scopeKey,
      requirementId,
      card: chosen,
      requirement: chosen?.requirements.find((row) => row.id === requirementId),
    })
  }

  useEffect(() => {
    setData((current) =>
      current?.target_type === target.type && current.target_id === target.id ? current : null
    )
    setRequirementRead(null)
    setDetailReadKey(null)
    setError(null)
    let cancelled = false
    apiFetch<DrawerResponse>(`/api/funds/${fundId}/objects/${target.type}/${target.id}`)
      .then((d) => {
        if (!cancelled) {
          setData(d)
          setDetailReadKey(evidenceReadKey)
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setData(null)
          setError(e)
        }
      })
    apiFetch<ObligationStatusRow[]>(
      `/api/evidence-status?object_type=${target.type}&object_id=${target.id}`
    )
      .then((r) => {
        if (!cancelled) setRequirementRead({ key: evidenceReadKey, rows: r })
      })
      .catch((failure: unknown) => {
        if (!cancelled) setRequirementRead({ key: evidenceReadKey, rows: null, error: failure })
      })
    return () => {
      cancelled = true
    }
  }, [fundId, target.type, target.id, tick, recordRevision, evidenceReadKey])

  const evidence = useMemo(() => data?.evidence ?? [], [data])
  const evidenceByDoc = useMemo(() => new Map(evidence.map((e) => [e.document_id, e])), [evidence])

  /* Read each cited document title once, including a failed/untitled result. */
  useEffect(() => {
    if (!rows || !data) return
    const missing = rows
      .map((r) => r.satisfied_by_document_id)
      .filter((id): id is string => !!id && !(id in docNames))
    if (missing.length === 0) return
    let cancelled = false
    Promise.all(missing.map(async (id) => [id, await documentName(id)] as const)).then((pairs) => {
      if (cancelled) return
      setDocNames((prev) => {
        const next = { ...prev }
        for (const [id, name] of pairs) next[id] = name
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [rows, data, docNames])

  /* The door to a paper carries the focus (the way back) and the CITED
   * PAGE (Ale 2026-09-03, "document at 2 clicks": the viewer opens where
   * the proof is). The page is the object's own evidence row on that
   * paper — the primary one when there are several; a paper the mentions
   * never cite opens at page 1 as before. */
  const docHref = (id: string, page: number | null = null) =>
    `/v2/documents/${id}?fund=${encodeURIComponent(fundId)}${
      section ? `&tab=${section.tab}&focus=${encodeURIComponent(section.focus)}` : ''
    }${page ? `&page=${page}` : ''}`
  const citedPage = (id: string | null): number | null => {
    if (!id) return null
    const cited = evidence.filter((e) => e.document_id === id && e.source_page)
    const ev = cited.find((e) => e.evidential_weight === 'primary') ?? cited[0]
    return ev?.source_page ?? null
  }
  const docName = (id: string | null): string | null => (id ? (docNames[id] ?? 'Document') : null)

  const ids = useMemo(() => new Set([target.id, ...relatedIds]), [target.id, relatedIds])
  const proven = (rows ?? []).filter((r) => PROVEN.has(r.status))
  const owedRows = (rows ?? []).filter((r) => OWED.has(r.status))
  const breaks = tasks.filter(
    (t) => RECON_TASK_TYPES.has(t.task_type) && t.target_id !== null && ids.has(t.target_id)
  )
  const walkOpen =
    target.type === 'entity'
      ? (walk.find((n) => n.entity_id === target.id && n.state === 'open') ?? null)
      : null
  const walkTask = walkOpen
    ? tasks.find((t) => t.task_type === 'look_through_review' && t.target_id === target.id)
    : undefined
  const chaseTaskFor = (row: ObligationStatusRow) =>
    tasks.find(
      (t) =>
        t.task_type === 'investigation_action' &&
        (t.target_id === row.id ||
          (t.task_data as Record<string, unknown> | null)?.evidence_status_id === row.id)
    )
  const signoffTaskFor = (row: ObligationStatusRow) =>
    tasks.find(
      (t) =>
        t.task_type === 'evidence_approval' &&
        (t.target_id === row.id ||
          (t.task_data as Record<string, unknown> | null)?.evidence_status_id === row.id)
    )
  const owedCount = owedRows.length + breaks.length + (walkOpen ? 1 : 0)
  /* SCALE (Ale 2026-09-03): the lists are bounded — PROVEN by the policy,
   * OWED by the gaps — but a page is a page: past five lines the rest is
   * one door to the Evidence tab on this object. */
  const evidenceHref = `/v2/funds/${encodeURIComponent(fundId)}?tab=evidence&focus=${encodeURIComponent(
    section?.focus ?? `${target.type === 'position' ? 'segment' : target.type}:${target.id}`
  )}`

  /* Mentions: the papers on file that name the object without proving a
   * demand — one door, never one row each. */
  const provenDocs = new Set(proven.map((r) => r.satisfied_by_document_id).filter(Boolean))
  const mentions = new Set(evidence.map((e) => e.document_id).filter((id) => !provenDocs.has(id)))
    .size

  /* The name: an entity or an instrument by its record name; a position as
   * "«instrument» in «issuer»". */
  const title = (() => {
    if (!data) return ''
    if (target.type === 'position') {
      const ins = pick(data.facts, 'instrument')
      const iss = pick(data.facts, 'issuer')
      if (ins && iss) return `${ins.charAt(0).toUpperCase()}${ins.slice(1)} in ${iss}`
    }
    return data.title
  })()
  /* The kind tag reads POSITION (Ale 2026-09-03 — "holding" is retired);
   * a commitment package carries a second tag saying so. */
  const kind = TARGET_WORD[target.type]
  const role =
    target.type === 'position' &&
    (data?.subtitle === 'commitment' || pick(data?.facts ?? {}, 'committed'))
      ? 'commitment'
      : null

  const provenLine = (row: ObligationStatusRow) => {
    const label = obligationLabel(row.obligation_code)
    const code = (
      <small className="sn-code" title="the policy code">
        {row.obligation_code}
      </small>
    )
    if (row.status === 'waived')
      return (
        <div
          key={row.id}
          className="drawer-ln"
          data-testid="drawer-proven-line"
          data-status="waived"
        >
          <Sentence
            meaning="drawer.proven.waived"
            values={{
              obligation: label,
              date: row.waived_at,
              reason: row.waived_reason?.replace(/[.\s]+$/, '') ?? null,
            }}
          >
            {code}
          </Sentence>
        </div>
      )
    const docId = row.satisfied_by_document_id
    const ev = docId ? evidenceByDoc.get(docId) : undefined
    const months = row.obligation?.stale_after_months
    const neverExpires = !!row.obligation && (months === null || months === undefined)
    const base = ev?.document_date ?? row.document_date ?? null
    const until = ev?.valid_to ?? (months && base ? addMonths(base, months) : null)
    const meaning = neverExpires
      ? 'drawer.proven.noexpiry'
      : until
        ? 'drawer.proven.until'
        : 'drawer.proven.onfile'
    return (
      <div
        key={row.id}
        className="drawer-ln"
        data-testid="drawer-proven-line"
        data-status="satisfied"
      >
        <LinkedSentence
          meaning={meaning}
          values={{ obligation: label, document: docName(docId), until }}
          slot="document"
          href={docId ? docHref(docId, citedPage(docId)) : null}
          linkTestId="drawer-proven-doc"
          documentRef={docId ? { document_id: docId } : null}
        >
          {code}
        </LinkedSentence>
      </div>
    )
  }

  const door = (label: string, to: string, testId = 'drawer-owed-door') => (
    <Link className="drawer-door" to={to} data-testid={testId}>
      {label} ›
    </Link>
  )
  const chaseHref = (task?: TaskQueueItem) =>
    workContext?.chaseHref ??
    `/v2/workbench/chase-request?fund=${fundId}${task ? `&task=${task.task_id}` : ''}`

  const owedLine = (row: ObligationStatusRow) => {
    const label = obligationLabel(row.obligation_code)
    const code = (
      <small className="sn-code" title="the policy code">
        {row.obligation_code}
      </small>
    )
    const meaning =
      row.status === 'requested'
        ? 'drawer.owed.requested'
        : row.status === 'pending_signoff'
          ? 'drawer.owed.pending'
          : 'drawer.owed.open'
    const direct =
      row.status !== 'pending_signoff' &&
      evidenceContext?.cards.some((card) =>
        card.requirements.some(
          (requirement) =>
            requirement.id === row.id &&
            requirement.target.kind === target.type &&
            requirement.target.id === target.id
        )
      )
    const verb = direct ? (
      <button
        type="button"
        className="drawer-door"
        data-testid="drawer-review-gap"
        disabled={!evidenceAvailable || evidencePending}
        onClick={() => chooseEvidence(row.id)}
      >
        Review evidence ›
      </button>
    ) : row.status === 'requested' ? (
      door('chase', chaseHref(chaseTaskFor(row)))
    ) : row.status === 'pending_signoff' ? (
      door(
        workContext ? 'Review close' : 'sign it off',
        workContext?.evidenceHref ??
          `/v2/workbench/ownership-verification?fund=${fundId}${
            signoffTaskFor(row) ? `&task=${signoffTaskFor(row)?.task_id}` : ''
          }`
      )
    ) : workContext ? (
      door('Review evidence', workContext.evidenceHref)
    ) : (
      door('request it', chaseHref(chaseTaskFor(row)))
    )
    return (
      <div
        key={row.id}
        className="drawer-ln"
        data-testid="drawer-owed-line"
        data-status={row.status}
      >
        <Sentence meaning={meaning} values={{ obligation: label }}>
          {code}
        </Sentence>{' '}
        {verb}
      </div>
    )
  }

  const breakLine = (task: TaskQueueItem) => {
    const taskData = (task.task_data ?? {}) as Record<string, unknown>
    const diag = (taskData.diagnosis ?? {}) as Record<string, unknown>
    const delta = taskDelta(task)
    /* The engine's own diagnosis in officer words: with a balance paper
     * newer than the last booked movement, the two dates and the amount
     * between them; a position whose units are money (a commitment) says
     * the currency — an instrument's break is in units of the paper. */
    const dated = !!diag.balance_as_of && !!diag.latest_trade_date
    const unitsMoney = /\s[A-Z]{3}$/.exec(pick(data?.facts ?? {}, 'units held', 'committed') ?? '')
    const currency = target.type === 'position' && unitsMoney ? unitsMoney[0].trim() : null
    const meaning = task.task_type.endsWith('trade_missing')
      ? dated
        ? 'drawer.owed.trade_missing.dated'
        : 'drawer.owed.trade_missing'
      : task.task_type.endsWith('balance_stale')
        ? diag.stored != null
          ? 'drawer.owed.balance_stale'
          : 'drawer.owed.balance_stale.nostored'
        : task.task_type.endsWith('rollforward')
          ? 'drawer.owed.rollforward'
          : 'drawer.owed.break'
    const amount = delta === null ? null : Math.abs(delta)
    return (
      <div
        key={task.task_id}
        className="drawer-ln"
        data-testid="drawer-owed-line"
        data-status="break"
        data-task={task.task_id}
      >
        <Sentence
          meaning={meaning}
          values={{
            delta: dated ? { amount, currency } : amount,
            balance_date: (diag.balance_as_of as string | undefined) ?? null,
            trade_date: (diag.latest_trade_date as string | undefined) ?? null,
            stored: (diag.stored as string | undefined) ?? null,
            computed: (diag.computed as string | undefined) ?? null,
          }}
        />{' '}
        {door('reconcile', `/v2/workbench/reconciliation?fund=${fundId}&task=${task.task_id}`)}
      </div>
    )
  }

  /* OWED, in one list: the demands, then the breaks, then the open walk. */
  const owedLines: React.ReactNode[] =
    rows === null
      ? []
      : [
          ...owedRows.map(owedLine),
          ...breaks.map(breakLine),
          ...(walkOpen
            ? [
                <div
                  key="walk"
                  className="drawer-ln"
                  data-testid="drawer-owed-line"
                  data-status="walk"
                >
                  <Sentence meaning="drawer.owed.walk" />{' '}
                  {door(
                    'Look-through',
                    /* The fund ALWAYS rides the door (B6 audit 2026-09-03) —
                     * without it the stream opened on every fund's segments. */
                    `/v2/workbench/record-keeping?fund=${fundId}${walkTask ? `&task=${walkTask.task_id}` : ''}`,
                    'drawer-walk-door'
                  )}
                </div>,
              ]
            : []),
        ]

  return (
    <>
      {/* the light scrim (3A): a tint over the page, never a wall — every
          click still lands on the record beneath */}
      <div className="fa-scrim" data-testid="drawer-scrim" aria-hidden="true" />
      <aside
        ref={panel}
        role="dialog"
        aria-label={title ? `${kind} — ${title}` : kind}
        tabIndex={-1}
        data-testid="object-drawer"
        className="fa-drawer"
        onSubmitCapture={(event) => {
          if (
            pendingEvidence.current &&
            event.target instanceof Element &&
            !event.target.closest('[data-testid="evidence-gap"]')
          ) {
            event.preventDefault()
            event.stopPropagation()
          }
        }}
        onClickCapture={(event) => {
          if (
            pendingEvidence.current &&
            event.target instanceof Element &&
            (event.target.closest('a') ||
              (event.target.closest('button') &&
                !event.target.closest('[data-testid="evidence-gap"]')))
          ) {
            event.preventDefault()
            event.stopPropagation()
          }
        }}
      >
        {/* one measurable body: the one-page budget is asserted on it */}
        <div className="drawer-body" data-testid="drawer-body">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="tag" data-testid="drawer-kind">
              {kind}
            </span>
            {role ? (
              <span className="tag" data-testid="drawer-kind-role">
                {role}
              </span>
            ) : null}
            <button
              className="vbtn"
              onClick={onClose}
              disabled={evidencePending}
              style={{ marginLeft: 'auto', padding: '2px 9px', fontSize: 'var(--text-12)' }}
              aria-label="Close drawer"
            >
              ✕
            </button>
          </div>

          {error ? (
            <ErrorState
              what="object"

              error={error}

              onRetry={() => setTick((t) => t + 1)}

              compact

              testId="drawer-error"
            />
          ) : null}

          {!data && !error ? <Skeleton kind="lines" n={6} /> : null}

          {data && (
            <>
              <h2 className="drawer-title" data-testid="drawer-title">
                {title} <HelpDot k="surface.object" />
              </h2>
              {section || onShowOnDiagram || onShowInRecord || addMovementHref ? (
                <div className="drawer-section" data-testid="drawer-section">
                  {section ? (
                    <div className="sentence">
                      {section.sentence} <HelpDot k="fund.focus" />
                    </div>
                  ) : null}
                  <div className="doors">
                    {addMovementHref &&
                    data?.target_type === target.type &&
                    data.target_id === target.id ? (
                      <Link className="vbtn" to={addMovementHref} data-testid="drawer-add-movement">
                        Add movement
                      </Link>
                    ) : null}
                    {onShowOnDiagram ? (
                      <button
                        type="button"
                        className="vbtn"
                        onClick={onShowOnDiagram}
                        disabled={evidencePending}
                        data-testid="drawer-show-diagram"
                      >
                        Show on diagram
                      </button>
                    ) : null}
                    {onShowInRecord ? (
                      <button
                        type="button"
                        className="vbtn"
                        onClick={onShowInRecord}
                        disabled={evidencePending}
                        data-testid="drawer-show-record"
                      >
                        Show in record
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {contextNavigation}
              <fieldset
                disabled={evidencePending}
                style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
              >
                {recordEditor}
              </fieldset>

              {canRemove ? (
                <div className="drawer-removal-actions">
                  <button
                    type="button"
                    className="vbtn"
                    data-testid="drawer-removal-action"
                    disabled={evidencePending}
                    aria-expanded={showRemoval}
                    onClick={() => setRemovalSelection(showRemoval ? null : removalKey)}
                  >
                    {showRemoval ? 'Close removal' : 'Remove from Record'}
                  </button>
                  {showRemoval ? (
                    <RemovalForm
                      target={target}
                      fundId={fundId}
                      targetLabel={title}
                      existingRequestIds={removalRequestIds}
                      onRequested={onRemovalRequested}
                      onRemoved={() => {
                        setRemovalSelection(null)
                        if (onRemoved) onRemoved()
                        else onClose()
                      }}
                    />
                  ) : null}
                </div>
              ) : null}

              <SectionHead first>What it is</SectionHead>
              <FactSentence target={target} title={data.title} facts={data.facts} />

              {requirementError ? (
                <ErrorState
                  what="evidence requirements"
                  error={requirementError}
                  onRetry={() => setTick((n) => n + 1)}
                  compact
                  testId="drawer-evidence-error"
                />
              ) : null}
              <SectionHead
                count={
                  requirementError
                    ? 'unavailable'
                    : rows
                      ? rows.length
                        ? `${proven.length} of ${rows.length}`
                        : 'none demanded'
                      : '…'
                }
                countTestId="drawer-proven-count"
              >
                Proven
              </SectionHead>
              {rows === null ? (
                <Skeleton kind="lines" n={2} />
              ) : (
                proven.slice(0, CAP).map(provenLine)
              )}
              {proven.length > CAP ? (
                <div className="drawer-ln also">
                  <Link className="drawer-door" to={evidenceHref} data-testid="drawer-proven-more">
                    … {proven.length - CAP} more ›
                  </Link>
                </div>
              ) : null}
              {mentions > 0 ? (
                <div className="drawer-ln also">
                  <Link
                    className="drawer-door"
                    to={`/v2/documents?fund=${encodeURIComponent(fundId)}&object=${target.type}:${target.id}`}
                    data-testid="drawer-also-named"
                  >
                    <Sentence meaning="drawer.also_named" values={{ documents: mentions }} /> ›
                  </Link>
                </div>
              ) : null}

              <SectionHead
                count={
                  requirementError
                    ? 'unavailable'
                    : rows
                      ? owedCount === 0
                        ? 'nothing'
                        : String(owedCount)
                      : '…'
                }
                countTestId="drawer-owed-count"
              >
                Owed
              </SectionHead>
              {rows === null ? null : owedCount === 0 ? (
                <div className="drawer-ln" data-testid="drawer-owed-line" data-status="nothing">
                  <Sentence meaning="drawer.nothing_owed" />
                </div>
              ) : (
                <>
                  {owedLines.slice(0, CAP)}
                  {owedLines.length > CAP ? (
                    <div className="drawer-ln also">
                      <Link
                        className="drawer-door"
                        to={evidenceHref}
                        data-testid="drawer-owed-more"
                      >
                        … {owedLines.length - CAP} more ›
                      </Link>
                    </div>
                  ) : null}
                </>
              )}
            </>
          )}
          {selection && evidenceContext ? (
            <section
              data-testid="drawer-evidence-review"
              aria-label="Selected evidence requirement"
            >
              <button
                type="button"
                className="drawer-door"
                disabled={evidencePending}
                onClick={() => setSelectedEvidence(null)}
              >
                Close evidence review ×
              </button>
              {!panelCard ? (
                <div>
                  <p>Choose the subchain for this evidence review.</p>
                  {choices.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      className="vbtn"
                      disabled={!evidenceAvailable || evidencePending}
                      onClick={() => chooseEvidence(selection.requirementId, card)}
                    >
                      {card.label}
                    </button>
                  ))}
                </div>
              ) : panelRequirement ? (
                <>
                  {!panelAvailable ? (
                    <p role="status">
                      {requirementError || error
                        ? 'Evidence details are unavailable. Retry before continuing.'
                        : evidenceContext.available && rows !== null
                          ? 'This requirement changed. Its recorded result remains below.'
                          : 'Refreshing the current evidence context…'}
                    </p>
                  ) : null}
                  <fieldset
                    disabled={!panelAvailable}
                    style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
                  >
                    <EvidenceGapPane
                      key={`${scopeKey}:${panelCard.id}:${panelRequirement.id}`}
                      requirement={panelRequirement}
                      targetLabel={title || kind}
                      fundId={fundId}
                      recordHref={panelCard.recordHref}
                      chaseHref={panelCard.chaseHref}
                      documents={
                        evidencePending
                          ? panelCard.documents
                          : (selectedCard?.documents ?? panelCard.documents)
                      }
                      onChanged={() => {
                        setTick((n) => n + 1)
                        evidenceContext.onChanged()
                      }}
                      onPendingChange={onEvidencePending}
                    />
                  </fieldset>
                </>
              ) : null}
            </section>
          ) : null}
        </div>
      </aside>
    </>
  )
}
